/**
 * NexOS runtime — vertical selector state + LIVE/SANDBOX mode toggle +
 * schema/KPI/feed hydration for the NexEngines Core Layout.
 *
 * Every network call carries the current `mode` so the whole OS re-skins
 * atomically when the operator flips LIVE → SANDBOX or picks a vertical.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/lib/auth";

const DEFAULT_VERTICAL = "all_engines";
const DEFAULT_MODE = "live";
const VALID_MODES = ["live", "sandbox"];

/* ─────────────────────────────────────────────────────────────────────
 * DEMO MOCK PAYLOAD
 * ---------------------------------------------------------------
 * When <NexOsProvider demoMock> is set (used on the public /demo
 * surface) EVERY metric/feed/KPI fetch is replaced with the fake data
 * below so no real customer briefs, cash balance, contact counts, or
 * live-feed events ever leak to anonymous visitors.
 *
 * Keep the shape identical to the real backend responses so downstream
 * components (KpiPulseBar, LiveSystemFeed, OpsKpiBar, TenStepRevenueFlow,
 * CapabilityRoadmap) render exactly the same chrome.
 * ───────────────────────────────────────────────────────────────── */
const DEMO_KPIS = [
  { id: "revenue",    label: "REVENUE BOOKED", icon: "dollar",    format: "currency", value: 148250, delta: 12, tone: "up", is_live: true },
  { id: "volume",     label: "NEW BRIEFS",     icon: "briefcase", format: "int",      value: 23,     delta: 4,  tone: "up", is_live: true },
  { id: "capacity",   label: "ACCEPTED",       icon: "users",     format: "int",      value: 11,     delta: 2,  tone: "up", is_live: true },
  { id: "unit_econ",  label: "AVG SCORE",      icon: "receipt",   format: "int",      value: 87,     delta: 3,  tone: "up", is_live: true },
];

const DEMO_FEED = [
  { id: "demo_evt_1", ts: new Date(Date.now() - 1000 * 60 * 2).toISOString(),  time: "2M AGO",  event: "Brief Submitted", details: "Prospect · Flow Engine · Trades · score 92/100",            status: "info" },
  { id: "demo_evt_2", ts: new Date(Date.now() - 1000 * 60 * 14).toISOString(), time: "14M AGO", event: "Payment Received", details: "Consultation fee · $499 · Stripe",                         status: "success" },
  { id: "demo_evt_3", ts: new Date(Date.now() - 1000 * 60 * 38).toISOString(), time: "38M AGO", event: "Preview Shipped",  details: "Hidden preview · booking-engine-demo · access-coded",     status: "info" },
  { id: "demo_evt_4", ts: new Date(Date.now() - 1000 * 60 * 65).toISOString(), time: "1H AGO",  event: "Brief Submitted", details: "Prospect · Capital Engine · FinServ · score 81/100",      status: "info" },
  { id: "demo_evt_5", ts: new Date(Date.now() - 1000 * 60 * 92).toISOString(), time: "1H AGO",  event: "Micro-Landing Visit", details: "4 views · flow-engine · utm_source=linkedin",         status: "info" },
  { id: "demo_evt_6", ts: new Date(Date.now() - 1000 * 60 * 145).toISOString(),time: "2H AGO",  event: "Brief Submitted", details: "Prospect · Booking Engine · Services · score 76/100",     status: "info" },
];

const DEMO_STEP_BADGES = {
  "01": "23", "02": "19", "03": "11", "04": "9",  "05": "7",
  "06": "6",  "07": "6",  "08": "5",  "09": "5",  "10": "4",
};

const DEMO_OPS_KPIS = {
  cash_on_hand:      { label: "CASH ON HAND",         icon: "dollar", format: "currency", value: 86420,  href: "/admin/finance" },
  credits_balance:   { label: "CREDITS BALANCE",      icon: "gauge",  format: "int",      value: 2450,   href: "/admin/billing" },
  total_contacts:    { label: "TOTAL CONTACTS",       icon: "users",  format: "int",      value: 148,    href: "/admin/contacts" },
  active_previews:   { label: "ACTIVE MICRO-LANDINGS",icon: "link",   format: "int",      value: 12,     href: "/admin/previews" },
  recent_documents:  { label: "DOCS · 7 DAYS",        icon: "file",   format: "int",      value: 34,     href: "/admin/documents" },
};

const DEMO_CAP_METRICS = {
  cap_01:{metric:"23 NEW BRIEFS",is_live:true},  cap_02:{metric:"FORM → CRM · READY",is_live:true},
  cap_03:{metric:"148 CONTACTS",is_live:true},   cap_04:{metric:"9 PAYMENTS",is_live:true},
  cap_05:{metric:"4 OPEN INVOICES",is_live:true},cap_06:{metric:"12 LIVE LANDINGS",is_live:true},
  cap_07:{metric:"STREAMING · LIVE",is_live:true},cap_08:{metric:"SCHEMA-DRIVEN",is_live:true},
  cap_09:{metric:"WIP · Q2",is_live:false},      cap_10:{metric:"3 SEATS",is_live:true},
  cap_11:{metric:"RBAC · ENFORCED",is_live:true},cap_12:{metric:"34 DOCS · 7D",is_live:true},
  cap_13:{metric:"2 PROJECTS",is_live:true},     cap_14:{metric:"8 TASKS OPEN",is_live:true},
  cap_15:{metric:"KANBAN · ACTIVE",is_live:true},cap_16:{metric:"AUDIT · LIVE",is_live:true},
  cap_17:{metric:"SMTP · HEALTHY",is_live:true}, cap_18:{metric:"SMS · DORMANT",is_live:false},
  cap_19:{metric:"WIP · Q3",is_live:false},      cap_20:{metric:"WIP · Q3",is_live:false},
  cap_21:{metric:"WIP · Q4",is_live:false},      cap_22:{metric:"WIP · Q4",is_live:false},
  cap_23:{metric:"WIP · Q4",is_live:false},      cap_24:{metric:"CUSTOM",is_live:false},
};

const Ctx = createContext({
  schema: null,
  vertical: DEFAULT_VERTICAL,
  setVertical: () => {},
  mode: DEFAULT_MODE,
  setMode: () => {},
  currentVertical: null,
  kpis: [],
  feed: [],
  stepBadges: {},
  loading: false,
  reloadSchema: () => {},
  reloadLive: () => {},
});

const readStored = (key, fallback, validator) => {
  try {
    const v = localStorage.getItem(key);
    if (v && (!validator || validator(v))) return v;
  } catch {}
  return fallback;
};

export const NexOsProvider = ({ children, demoMock = false }) => {
  const [schema, setSchema] = useState(null);
  const [vertical, setVertical] = useState(() => readStored("nex_os_vertical", DEFAULT_VERTICAL));
  const [mode, setMode] = useState(() => readStored("nex_os_mode", DEFAULT_MODE, (m) => VALID_MODES.includes(m)));
  const [kpis, setKpis] = useState(demoMock ? DEMO_KPIS : []);
  const [feed, setFeed] = useState(demoMock ? DEMO_FEED : []);
  const [stepBadges, setStepBadges] = useState(demoMock ? DEMO_STEP_BADGES : {});
  const [opsKpis, setOpsKpis] = useState(demoMock ? DEMO_OPS_KPIS : {});
  const [capMetrics, setCapMetrics] = useState(demoMock ? DEMO_CAP_METRICS : {});
  const [loading, setLoading] = useState(false);

  const reloadSchema = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/nex-os/schema`);
      setSchema(data.schema);
      const ids = (data.schema?.verticals || []).map((v) => v.id);
      if (ids.length && !ids.includes(vertical)) {
        setVertical(ids[0]);
      }
    } catch { /* silent */ }
  }, [vertical]);

  const reloadOps = useCallback(async () => {
    if (demoMock) return;
    try {
      const [{ data: kpiData }, { data: metricData }] = await Promise.all([
        axios.get(`${API}/nex-os/ops-kpis`),
        axios.get(`${API}/nex-os/capability-metrics`),
      ]);
      setOpsKpis(kpiData.kpis || {});
      setCapMetrics(metricData.metrics || {});
    } catch { /* silent */ }
  }, [demoMock]);

  const reloadLive = useCallback(async (v, m) => {
    if (demoMock) return;
    const targetV = v || vertical;
    const targetM = m || mode;
    setLoading(true);
    try {
      const params = { vertical: targetV, mode: targetM };
      const [kRes, fRes, sRes] = await Promise.all([
        axios.get(`${API}/nex-os/kpis`,        { params }),
        axios.get(`${API}/nex-os/live-feed`,   { params }),
        axios.get(`${API}/nex-os/step-counts`, { params }),
      ]);
      setKpis(kRes.data.kpis || []);
      setFeed(fRes.data.events || []);
      setStepBadges(sRes.data.badges || {});
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [vertical, mode, demoMock]);

  useEffect(() => { reloadSchema(); reloadOps(); }, [reloadSchema, reloadOps]);

  useEffect(() => {
    if (!schema || demoMock) return;
    reloadLive(vertical, mode);
    const t = setInterval(() => {
      reloadLive(vertical, mode);
      reloadOps();
    }, 30000);
    return () => clearInterval(t);
  }, [schema, vertical, mode, reloadLive, reloadOps, demoMock]);

  useEffect(() => { try { localStorage.setItem("nex_os_vertical", vertical); } catch {} }, [vertical]);
  useEffect(() => { try { localStorage.setItem("nex_os_mode", mode); } catch {} }, [mode]);

  const currentVertical = useMemo(() => {
    if (!schema) return null;
    return schema.verticals.find((v) => v.id === vertical) || schema.verticals[0];
  }, [schema, vertical]);

  const value = useMemo(() => ({
    schema, vertical, setVertical, mode, setMode, currentVertical,
    kpis, feed, stepBadges, opsKpis, capMetrics, loading, reloadSchema, reloadLive, reloadOps,
  }), [schema, vertical, mode, currentVertical, kpis, feed, stepBadges, opsKpis, capMetrics, loading, reloadSchema, reloadLive, reloadOps]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useNexOs = () => useContext(Ctx);

/** Format helper — mirrors backend "format" hint. */
export const formatKpi = (v, format) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  if (format === "currency") {
    if (Math.abs(n) >= 1000) {
      return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    }
    if (n === 0) return "$0";
    return `$${n.toFixed(2)}`;
  }
  if (format === "percent") return `${Math.round(n)}%`;
  return n.toLocaleString();
};
